import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { DeconvolutionOperation, Study } from 'interfaces/study';
import { every, filter, map, split } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { humanize } from 'utils/helpers';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import {
  deconvolutionTypeToDefaultCoefficients,
  regexTestIsJSONFloat3x20Matrix,
  regexTestIsJSONFloat3x3Matrix,
  regexTestIsRGBVector,
} from '../helpers';

export const DeconvolutionOperationComponent: React.FC<{
  componentIndex: number;
  componentOperation: DeconvolutionOperation;
  updatedSettings: Study['settings'];
  setUpdatedSettings: React.Dispatch<React.SetStateAction<Study['settings']>>;
  stainTypeId: string;
}> = ({ componentIndex, componentOperation, updatedSettings, setUpdatedSettings, stainTypeId }) => {
  const updateComponentField = useCallback(
    (field: keyof DeconvolutionOperation, newValue: DeconvolutionOperation[keyof DeconvolutionOperation]) =>
      setUpdatedSettings({
        ...updatedSettings,
        deconvolutionSettings: {
          ...updatedSettings.deconvolutionSettings,
          stainToComponentOperation: {
            ...updatedSettings.deconvolutionSettings.stainToComponentOperation,
            [stainTypeId]: map(
              updatedSettings.deconvolutionSettings.stainToComponentOperation?.[stainTypeId],
              (operation, index) => (index === componentIndex ? { ...operation, [field]: newValue } : operation)
            ),
          },
        },
      }),
    [updatedSettings, stainTypeId]
  );

  const stainComponentsIds = useMemo(() => split(stainTypeId, '_'), [stainTypeId]);
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();

  const [isEditingDisplayName, setIsEditingDisplayName] = React.useState(!componentOperation?.displayName);

  const currentDeconvolutionType = componentOperation?.deconvolutionType || 'cubic';
  const currentDisplayName =
    componentOperation?.displayName ??
    `${stainTypeIdToDisplayName(componentOperation?.componentStainTypeId)} (${humanize(currentDeconvolutionType)})`;

  const [temporaryDisplayName, setTemporaryDisplayName] = React.useState(currentDisplayName);
  const isUniqueDisplayName = every(
    updatedSettings.deconvolutionSettings?.stainToComponentOperation?.[stainTypeId],
    (operation, checkedIndex) => operation.displayName !== temporaryDisplayName || checkedIndex === componentIndex
  );

  const currentCoefficients = useMemo(
    () =>
      JSON.stringify(
        componentOperation?.coefficients || deconvolutionTypeToDefaultCoefficients(currentDeconvolutionType)
      ),
    [componentOperation?.coefficients]
  );
  const [temporaryCoefficients, setTemporaryCoefficients] = React.useState('');
  const currentIntercept = useMemo(
    () => JSON.stringify(componentOperation?.intercept || [0, 0, 0]),
    [componentOperation?.intercept]
  );
  const [temporaryIntercept, setTemporaryIntercept] = React.useState('');

  const regexTest =
    currentDeconvolutionType === 'cubic' ? regexTestIsJSONFloat3x20Matrix : regexTestIsJSONFloat3x3Matrix;
  const isValidCoefficients = !temporaryCoefficients || regexTest.test(temporaryCoefficients);
  const isValidIntercept = !temporaryIntercept || regexTestIsRGBVector.test(temporaryIntercept);

  const confirmWithModal = useConfirmation();

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandIcon />}>
        <Grid container xs={12} direction="row" justifyContent="space-between" alignItems="center">
          <Grid item xs={10}>
            {isEditingDisplayName ? (
              <TextField
                label="Display Name"
                value={temporaryDisplayName}
                onChange={(event) => setTemporaryDisplayName(event.target.value)}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                }}
                autoFocus
              />
            ) : (
              <Typography variant="subtitle1">
                {currentDisplayName ||
                  `${componentOperation?.componentStainTypeId} (${humanize(currentDeconvolutionType)})`}
              </Typography>
            )}
          </Grid>
          <Grid item xs={2}>
            <IconButton
              disabled={!isUniqueDisplayName}
              color={isEditingDisplayName ? 'primary' : 'default'}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                if (isEditingDisplayName && temporaryDisplayName) {
                  updateComponentField('displayName', temporaryDisplayName);
                } else {
                  setTemporaryDisplayName(currentDisplayName);
                }
                setIsEditingDisplayName((prev) => !prev);
              }}
            >
              {isEditingDisplayName && temporaryDisplayName ? (
                <CheckIcon />
              ) : isEditingDisplayName ? (
                <CloseIcon />
              ) : (
                <EditIcon />
              )}
            </IconButton>
            {!isUniqueDisplayName && (
              <Typography variant="caption" color="error">
                Display name must be unique
              </Typography>
            )}
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={10} direction="row">
            <LabelledDropdown
              label="Select Component"
              options={map(stainComponentsIds, (stainComponentId) => ({
                text: stainTypeIdToDisplayName(stainComponentId),
                value: stainComponentId,
              }))}
              value={componentOperation?.componentStainTypeId}
              onOptionSelected={(newValue) => updateComponentField('componentStainTypeId', newValue)}
            />
          </Grid>
          <Grid item xs={10} direction="row">
            <LabelledDropdown
              label="Select Operation"
              options={[
                { text: 'Linear', value: 'linear' },
                { text: 'Cubic', value: 'cubic' },
              ]}
              value={currentDeconvolutionType}
              onOptionSelected={(newValue: DeconvolutionOperation['deconvolutionType']) => {
                updateComponentField('deconvolutionType', newValue);
                updateComponentField('coefficients', deconvolutionTypeToDefaultCoefficients(newValue));
              }}
            />
          </Grid>
          <Grid item container xs={12} direction="row" justifyContent="space-between" alignItems="center">
            <Grid item xs={10}>
              <TextField
                label="Coefficients"
                value={temporaryCoefficients || currentCoefficients}
                onChange={(event) => {
                  const newValue = event.target.value;
                  setTemporaryCoefficients(newValue);
                }}
              />
            </Grid>
            {Boolean(temporaryCoefficients) && temporaryCoefficients !== currentCoefficients && (
              <Grid item xs={2}>
                <IconButton
                  disabled={
                    !temporaryCoefficients || temporaryCoefficients === currentCoefficients || !isValidCoefficients
                  }
                  color={isValidCoefficients ? 'success' : temporaryCoefficients ? 'error' : undefined}
                  onClick={() => {
                    if (isValidCoefficients) {
                      updateComponentField('coefficients', JSON.parse(temporaryCoefficients));
                      setTemporaryCoefficients('');
                    }
                  }}
                >
                  {isValidCoefficients ? <CheckIcon /> : <CloseIcon />}
                </IconButton>
                {!isValidCoefficients && (
                  <Typography variant="caption" color="error">
                    Invalid JSON array
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
          <Grid item container xs={12} direction="row" justifyContent="space-between" alignItems="center">
            <Grid item xs={10}>
              <TextField
                label="Intercept"
                value={temporaryIntercept || currentIntercept}
                onChange={(event) => {
                  const newValue = event.target.value;
                  setTemporaryIntercept(newValue);
                }}
              />
            </Grid>
            {Boolean(temporaryIntercept) && temporaryIntercept !== currentIntercept && (
              <Grid item xs={2}>
                <IconButton
                  disabled={!temporaryIntercept || temporaryIntercept === currentIntercept || !isValidIntercept}
                  color={isValidIntercept ? 'success' : temporaryIntercept ? 'error' : undefined}
                  onClick={() => {
                    if (isValidIntercept) {
                      updateComponentField('intercept', JSON.parse(temporaryIntercept));
                      setTemporaryIntercept('');
                    }
                  }}
                >
                  {isValidIntercept ? <CheckIcon /> : <CloseIcon />}
                </IconButton>
                {!isValidIntercept && (
                  <Typography variant="caption" color="error">
                    Invalid JSON array
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid container mt={2} direction="row" justifyContent="flex-end">
          <Button
            color="error"
            onClick={async () => {
              if (
                await confirmWithModal({
                  title: 'Are you sure you want to remove this operation?',
                })
              ) {
                setUpdatedSettings({
                  ...updatedSettings,
                  deconvolutionSettings: {
                    ...updatedSettings.deconvolutionSettings,
                    stainToComponentOperation: {
                      ...updatedSettings.deconvolutionSettings.stainToComponentOperation,
                      [stainTypeId]: filter(
                        updatedSettings.deconvolutionSettings.stainToComponentOperation?.[stainTypeId],
                        (_, index) => index !== componentIndex
                      ),
                    },
                  },
                });
              }
            }}
          >
            Remove Operation
          </Button>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
